import {
  RISK_ASSESSMENT_HOUSEKEEPING_CHILD_SECTION_IDS,
  RISK_ASSESSMENT_HOUSEKEEPING_PARENT_SECTION_ID,
  SECTION_IDS,
} from './riskAssessmentSections'
import type {
  ComplianceDocumentSectionKind,
  ComplianceDocumentSectionRecord,
} from './complianceDocumentSectionRecord'
import {
  parseMarkdownHeadingLine,
  stripRedundantSectionHeadingFromBody,
} from './stripRedundantSectionHeadingFromBody'
import { unwrapOuterMarkdownCodeFence } from './unwrapOuterMarkdownCodeFence'

export { stripRedundantSectionHeadingFromBody }

export type RiskAssessmentSectionKind = ComplianceDocumentSectionKind

export type RiskAssessmentSectionRecord = ComplianceDocumentSectionRecord

const CANONICAL_SECTION_ID_SET = new Set<string>(SECTION_IDS)

export function isCanonicalRiskAssessmentSectionId(
  sectionId: string
): boolean {
  return CANONICAL_SECTION_ID_SET.has(sectionId)
}

function inferSectionKind(sectionId: string): RiskAssessmentSectionKind {
  return isCanonicalRiskAssessmentSectionId(sectionId) ? 'canonical' : 'custom'
}

/** User-authored appendix ids (`appendix-1`, `appendix-a`, …). */
export function isCustomRiskAssessmentAppendixSectionId(
  sectionId: string
): boolean {
  return /^appendix-/i.test(sectionId)
}

function isTopLevelRiskAssessmentSectionHeading(sectionId: string): boolean {
  return (
    (isCanonicalRiskAssessmentSectionId(sectionId) &&
      !isRiskAssessmentChildSectionId(sectionId)) ||
    isCustomRiskAssessmentAppendixSectionId(sectionId)
  )
}

export function isRiskAssessmentChildSectionId(sectionId: string): boolean {
  return sectionId.includes('.') && sectionId.split('.').length > 2
}

export function isRiskAssessmentChildSectionRecord(section: {
  sectionId: string
  parentSectionId?: string | null
}): boolean {
  if (section.parentSectionId != null && section.parentSectionId.length > 0) {
    return true
  }
  return isRiskAssessmentChildSectionId(section.sectionId)
}

function stripOrchestratorSectionHeadingFromGeneratedMarkdown(
  sectionId: string,
  generatedMarkdown: string,
  options?: { parentSectionId?: string | null }
): string {
  const lines = generatedMarkdown.split('\n')
  let index = 0

  while (index < lines.length && lines[index].trim() === '') {
    index += 1
  }

  if (index >= lines.length) {
    return generatedMarkdown.trim()
  }

  const heading = parseHeadingLine(lines[index])
  if (heading?.sectionId === sectionId) {
    index += 1
    while (index < lines.length && lines[index].trim() === '') {
      index += 1
    }
    return lines.slice(index).join('\n').trim()
  }

  if (options?.parentSectionId != null) {
    const childHeading = parseMarkdownHeadingLine(lines[index])
    if (childHeading != null && childHeading.level === 3) {
      index += 1
      while (index < lines.length && lines[index].trim() === '') {
        index += 1
      }
      return lines.slice(index).join('\n').trim()
    }
  }

  return generatedMarkdown.trim()
}

/**
 * Build one canonical section record from orchestrator output for a single section run.
 */
export function buildRiskAssessmentSectionRecordFromOrchestratorChunk(options: {
  sectionId: string
  parentSectionId?: string | null
  title: string
  generatedMarkdown: string
  sortOrder: number
}): RiskAssessmentSectionRecord {
  const bodyWithoutChromeHeading =
    stripOrchestratorSectionHeadingFromGeneratedMarkdown(
      options.sectionId,
      options.generatedMarkdown,
      { parentSectionId: options.parentSectionId }
    )

  return normalizeRiskAssessmentSectionRecord({
    sectionId: options.sectionId,
    parentSectionId: options.parentSectionId ?? null,
    title: options.title,
    kind: inferSectionKind(options.sectionId),
    sortOrder: options.sortOrder,
    markdown: bodyWithoutChromeHeading,
  })
}

function parseHeadingLine(line: string): { sectionId: string; title: string } | null {
  if (!line.startsWith('## ')) {
    return null
  }
  const rest = line.slice(3).trimStart()
  const spaceIndex = rest.indexOf(' ')
  if (spaceIndex === -1) {
    return { sectionId: rest, title: '' }
  }
  return {
    sectionId: rest.slice(0, spaceIndex),
    title: rest.slice(spaceIndex + 1).trim(),
  }
}

export function normalizeRiskAssessmentSectionRecord(
  section: RiskAssessmentSectionRecord
): RiskAssessmentSectionRecord {
  const markdown = unwrapOuterMarkdownCodeFence(
    stripRedundantSectionHeadingFromBody(
      section.markdown,
      section.sectionId,
      section.title
    )
  )

  return {
    ...section,
    parentSectionId: section.parentSectionId ?? null,
    markdown,
  }
}

export function normalizeRiskAssessmentSections(
  sections: RiskAssessmentSectionRecord[]
): RiskAssessmentSectionRecord[] {
  return sections.map(normalizeRiskAssessmentSectionRecord)
}

function parseSubsectionBlocks(
  markdown: string
): Array<{ title: string; body: string }> {
  const lines = markdown.split('\n')
  let index = 0
  const blocks: Array<{ title: string; body: string }> = []

  while (index < lines.length) {
    const heading = parseMarkdownHeadingLine(lines[index])
    if (heading == null || heading.level !== 3) {
      index += 1
      continue
    }

    const bodyLines: string[] = []
    index += 1
    while (index < lines.length) {
      const nextHeading = parseMarkdownHeadingLine(lines[index])
      if (nextHeading != null && nextHeading.level <= 3) {
        break
      }
      bodyLines.push(lines[index])
      index += 1
    }

    blocks.push({
      title: heading.text,
      body: bodyLines.join('\n').trim(),
    })
  }

  return blocks
}

/**
 * Split a legacy monolithic `7.3` record (body with `###` subsections) into parent + child records.
 */
export function expandLegacyHousekeepingSectionRecords(
  sections: RiskAssessmentSectionRecord[]
): RiskAssessmentSectionRecord[] {
  const hasChildSections = sections.some((section) =>
    RISK_ASSESSMENT_HOUSEKEEPING_CHILD_SECTION_IDS.includes(
      section.sectionId as (typeof RISK_ASSESSMENT_HOUSEKEEPING_CHILD_SECTION_IDS)[number]
    )
  )
  if (hasChildSections) {
    return sections
  }

  const parentIndex = sections.findIndex(
    (section) => section.sectionId === RISK_ASSESSMENT_HOUSEKEEPING_PARENT_SECTION_ID
  )
  if (parentIndex === -1) {
    return sections
  }

  const parentSection = sections[parentIndex]
  const subsectionBlocks = parseSubsectionBlocks(parentSection.markdown)
  if (subsectionBlocks.length === 0) {
    return sections
  }

  const preambleLines: string[] = []
  const lines = parentSection.markdown.split('\n')
  let lineIndex = 0
  while (lineIndex < lines.length) {
    const heading = parseMarkdownHeadingLine(lines[lineIndex])
    if (heading != null && heading.level === 3) {
      break
    }
    preambleLines.push(lines[lineIndex])
    lineIndex += 1
  }

  const updatedParent: RiskAssessmentSectionRecord = {
    ...parentSection,
    parentSectionId: null,
    markdown: preambleLines.join('\n').trim(),
  }

  const childRecords = RISK_ASSESSMENT_HOUSEKEEPING_CHILD_SECTION_IDS.map(
    (childSectionId, childIndex) => {
      const existingChild = sections.find(
        (section) => section.sectionId === childSectionId
      )
      const block = subsectionBlocks[childIndex]
      return normalizeRiskAssessmentSectionRecord({
        sectionId: childSectionId,
        parentSectionId: RISK_ASSESSMENT_HOUSEKEEPING_PARENT_SECTION_ID,
        title: existingChild?.title ?? block?.title ?? childSectionId,
        kind: 'canonical',
        sortOrder: parentSection.sortOrder + childIndex + 1,
        markdown: block?.body ?? '',
      })
    }
  )

  const beforeParent = sections.slice(0, parentIndex)
  const afterParent = sections.slice(parentIndex + 1)
  const merged = [...beforeParent, updatedParent, ...childRecords, ...afterParent]

  return merged.map((section, index) => ({
    ...section,
    sortOrder: index,
  }))
}

/** Ensure child housekeeping sections carry parentSectionId for compose/UI. */
export function normalizeRiskAssessmentSectionParentLinks(
  sections: RiskAssessmentSectionRecord[]
): RiskAssessmentSectionRecord[] {
  return sections.map((section) => {
    if (
      section.parentSectionId == null &&
      isRiskAssessmentChildSectionId(section.sectionId)
    ) {
      return {
        ...section,
        parentSectionId: RISK_ASSESSMENT_HOUSEKEEPING_PARENT_SECTION_ID,
      }
    }
    return section
  })
}

/**
 * Split markdown on line-start `## ` headings into preamble + section records.
 */
export function parseRiskAssessmentSections(markdown: string): {
  preamble: string
  sections: RiskAssessmentSectionRecord[]
} {
  const lines = markdown.split('\n')
  let index = 0
  const preambleLines: string[] = []

  while (index < lines.length) {
    const line = lines[index]
    if (line.startsWith('## ')) {
      break
    }
    preambleLines.push(line)
    index += 1
  }

  const sections: RiskAssessmentSectionRecord[] = []
  let sortOrder = 0

  while (index < lines.length) {
    const line = lines[index]
    if (!line.startsWith('## ')) {
      index += 1
      continue
    }

    const heading = parseHeadingLine(line)
    if (heading == null || heading.sectionId === '') {
      index += 1
      continue
    }

    const bodyLines: string[] = []
    index += 1
    while (index < lines.length && !lines[index].startsWith('## ')) {
      bodyLines.push(lines[index])
      index += 1
    }

    if (!isTopLevelRiskAssessmentSectionHeading(heading.sectionId)) {
      const previousIndex = sections.length - 1
      const previousSection =
        previousIndex >= 0 ? sections[previousIndex] : undefined
      if (previousSection != null) {
        const foldedBlock = [line, ...bodyLines].join('\n').trim()
        previousSection.markdown =
          previousSection.markdown.trim().length > 0
            ? `${previousSection.markdown.trimEnd()}\n\n${foldedBlock}`
            : foldedBlock
        continue
      }
    }

    sections.push(
      normalizeRiskAssessmentSectionRecord({
        sectionId: heading.sectionId,
        parentSectionId: null,
        title: heading.title,
        kind: inferSectionKind(heading.sectionId),
        sortOrder,
        markdown: bodyLines.join('\n').trim(),
      })
    )
    sortOrder += 1
  }

  return {
    preamble: preambleLines.join('\n').trimEnd(),
    sections: expandLegacyHousekeepingSectionRecords(sections),
  }
}

export function formatRiskAssessmentSectionBlock(
  section: RiskAssessmentSectionRecord,
): string {
  if (section.parentSectionId != null && section.parentSectionId.length > 0) {
    const heading =
      section.title.trim().length > 0
        ? `### ${section.sectionId} ${section.title}`
        : `### ${section.sectionId}`
    const body = section.markdown.trimEnd()
    if (body.length === 0) {
      return heading
    }
    return `${heading}\n\n${body}`
  }

  let heading: string
  if (section.kind === 'custom') {
    heading =
      section.title.trim().length > 0
        ? `## ${section.title}`
        : `## ${section.sectionId}`
  } else if (section.title.trim().length > 0) {
    heading = `## ${section.sectionId} ${section.title}`
  } else {
    heading = `## ${section.sectionId}`
  }
  const body = section.markdown.trimEnd()
  if (body.length === 0) {
    return heading
  }
  return `${heading}\n\n${body}`
}

/**
 * Compose full document markdown from preamble + ordered section records.
 */
export function composeRiskAssessmentMarkdown(
  preamble: string,
  sections: RiskAssessmentSectionRecord[]
): string {
  const sortedSections = [...sections].sort(
    (left, right) => left.sortOrder - right.sortOrder
  )
  const parts: string[] = []
  const trimmedPreamble = preamble.trimEnd()
  if (trimmedPreamble.length > 0) {
    parts.push(trimmedPreamble)
  }

  for (const section of sortedSections) {
    const block = formatRiskAssessmentSectionBlock(section)
    if (block.trim().length === 0) {
      continue
    }
    if (parts.length > 0) {
      parts.push('')
    }
    parts.push(block)
  }

  return parts.join('\n')
}
